import React from 'react';
import { Box, Button, MenuItem, Select, Stack, useTheme } from '@mui/material';
import { ItemCategory } from '@prisma/client';
import { toast } from 'react-toastify';
import { Virtuoso } from 'react-virtuoso';
import { getTextForCategory } from '../../helpers/items';
import ItemsSearchInput from './ItemsSearchInput';
import ProductForm from './ProductForm';
import ItemSideBarModal from './ItemSideBarModal';
import FilterChips from '../checkout/catalog/FilterChips';
import Error from '../global/Error';
import OutlinedPageBox from '../global/OutlinedPageBox';
import CenteredContentLayout from '../global/CenteredContentLayout';
import PageLoading from '../global/PageLoading';
import FullPageModal from '../modal/FullPageModal';
import LibraryHeader from './library/LibraryHeader';
import LibraryProductRow from './library/LibraryProductRow';
import LibraryVariantRow from './library/LibraryVariantRow';
import { LibraryRow, libraryRowKey } from './library/layout';
import useConfirm from '../../hooks/useConfirm';
import useCatalog from '../../hooks/catalog/useCatalog';
import useAttributes from '../../hooks/catalog/useAttributes';
import useItems from '../../hooks/items/useItems';
import useUpdateItem from '../../hooks/items/useUpdateItem';
import useDeleteItem from '../../hooks/items/useDeleteItem';
import useSetProductTaxable from '../../hooks/products/useSetProductTaxable';
import { invalidateCatalog } from '../../services/catalog';
import { ItemWithCommissions } from '../../types/items';
import { trimString } from '../../helpers/global';
import {
  EMPTY_SELECTION,
  NO_BRAND_ID,
  PickerSelection,
  ProductInView,
  brandTiles,
  buildFilterChips,
  categoryTiles,
  flattenCatalog,
  productsInView,
  rowMatches,
  sortVariants,
  typeTiles,
} from '../../utils/catalog/catalogFilters';
import {
  buildSearchIndex,
  searchCatalog,
} from '../../utils/catalog/searchCatalog';

function ProductsTab() {
  const theme = useTheme();
  const confirm = useConfirm();
  const catalogQuery = useCatalog();
  const attributesQuery = useAttributes();
  const itemsQuery = useItems();

  const [query, setQuery] = React.useState('');
  const [selection, setSelection] =
    React.useState<PickerSelection>(EMPTY_SELECTION);
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const [editing, setEditing] = React.useState<
    | { kind: 'new' }
    | { kind: 'product'; productId: string; name: string }
    | { kind: 'standalone'; item: ItemWithCommissions }
    | null
  >(null);
  const [selectedViewItemId, setSelectedViewItemId] = React.useState<
    string | null
  >(null);

  const catalog = React.useMemo(
    () => catalogQuery.data ?? [],
    [catalogQuery.data],
  );
  const attributes = React.useMemo(
    () => attributesQuery.data ?? [],
    [attributesQuery.data],
  );
  const itemsById = React.useMemo(
    () => new Map((itemsQuery.data ?? []).map(item => [item.id, item])),
    [itemsQuery.data],
  );
  const catalogById = React.useMemo(
    () => new Map(catalog.map(product => [product.id, product])),
    [catalog],
  );
  const rows = React.useMemo(() => flattenCatalog(catalog), [catalog]);
  const searchIndex = React.useMemo(() => buildSearchIndex(catalog), [catalog]);
  const categories = React.useMemo(() => categoryTiles(catalog), [catalog]);
  const brands = React.useMemo(
    () => brandTiles(rows, selection),
    [rows, selection],
  );
  const types = React.useMemo(
    () => typeTiles(rows, selection),
    [rows, selection],
  );
  const chips = React.useMemo(
    () => buildFilterChips(rows, attributes, selection),
    [rows, attributes, selection],
  );
  const deferredQuery = React.useDeferredValue(query);
  const isSearchActive = !!deferredQuery.trim();
  const products = React.useMemo<ProductInView[]>(() => {
    const matched = isSearchActive
      ? searchCatalog(
          searchIndex.filter(entry => rowMatches(entry.row, selection)),
          deferredQuery,
          Number.MAX_SAFE_INTEGER,
        )
      : productsInView(rows, selection);
    return matched.map(entry => ({
      ...entry,
      variants: sortVariants(entry.variants, attributes),
    }));
  }, [isSearchActive, searchIndex, rows, selection, deferredQuery, attributes]);
  const libraryRows = React.useMemo<LibraryRow[]>(
    () =>
      products.flatMap(entry => [
        { kind: 'product' as const, entry },
        ...(expanded.has(entry.product.id)
          ? entry.variants.map(variant => ({
              kind: 'variant' as const,
              entry,
              variant,
            }))
          : []),
      ]),
    [products, expanded],
  );

  const { mutate: updateItem, isPending: isUpdating } = useUpdateItem({
    onSuccess: () => invalidateCatalog(),
    onError: err => {
      toast.error(err);
    },
  });
  const { mutate: deleteItem, isPending: isDeleting } = useDeleteItem({
    onSuccess: () => invalidateCatalog(),
    onError: err => {
      toast.error(err);
    },
  });
  const { mutate: setProductTaxable, isPending: isSettingTaxable } =
    useSetProductTaxable({ onError: err => toast.error(err) });
  const busy = isUpdating || isDeleting || isSettingTaxable;

  const toggleExpanded = (productId: string) =>
    setExpanded(current => {
      const next = new Set(current);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });

  const toggleAttributeValue = (key: string, label: string) =>
    setSelection(current => {
      const values = current.attributeValues[key] ?? [];
      return {
        ...current,
        attributeValues: {
          ...current.attributeValues,
          [key]: values.includes(label)
            ? values.filter(v => v !== label)
            : [...values, label],
        },
      };
    });

  const clearAttribute = (key: string) =>
    setSelection(current => ({
      ...current,
      attributeValues: Object.fromEntries(
        Object.entries(current.attributeValues).filter(([k]) => k !== key),
      ),
    }));

  const selectCategory = (category: ItemCategory | null) =>
    setSelection({ ...EMPTY_SELECTION, category });

  const filterSelect = (
    label: string,
    value: string | null,
    options: { key: string; label: string; count: number }[],
    onChange: (value: string | null) => void,
  ) => (
    <Select
      size="medium"
      displayEmpty
      value={value ?? ''}
      onChange={e => onChange((e.target.value as string) || null)}
      className="filter-select"
      sx={{
        minWidth: 160,
        borderRadius: '10px',
        bgcolor: theme.palette.background.paper,
      }}>
      <MenuItem value="">All {label.toLowerCase()}</MenuItem>
      {options.map(option => (
        <MenuItem key={option.key} value={option.key}>
          {option.label} ({option.count})
        </MenuItem>
      ))}
    </Select>
  );

  const editProduct = (entry: ProductInView) => {
    if (!entry.product.isStandalone) {
      setEditing({
        kind: 'product',
        productId: entry.product.id,
        name: entry.product.name,
      });
      return;
    }
    // the items list can lag the catalog for a moment after a save
    const item = itemsById.get(entry.product.id);
    if (item) setEditing({ kind: 'standalone', item });
  };

  const productActions = (entry: ProductInView) => {
    const { product } = entry;
    // the row may be filtered, so read taxability across every variant
    const variantIds = (catalogById.get(product.id)?.variants ?? []).map(
      variant => variant.id,
    );
    const isTaxable = variantIds.every(id => itemsById.get(id)?.isTaxable);
    return [
      { id: 'edit', label: 'Edit', action: () => editProduct(entry) },
      {
        id: 'taxable',
        label: isTaxable ? 'Make non-taxable' : 'Make taxable',
        action: () =>
          product.isStandalone
            ? updateItem({
                itemId: product.id,
                payload: { isTaxable: !isTaxable },
              })
            : setProductTaxable({
                productId: product.id,
                isTaxable: !isTaxable,
              }),
      },
    ];
  };

  const variantActions = (item: ItemWithCommissions) => [
    {
      id: 'favorite',
      label: item.favoritedAt ? 'Unfavorite' : 'Favorite',
      action: () =>
        updateItem({
          itemId: item.id,
          payload: { favorite: !item.favoritedAt },
        }),
    },
    {
      id: 'delete',
      label: 'Delete',
      action: async () => {
        const confirmed = await confirm({
          title: 'Delete Item',
          message: `Are you sure you want to delete ${item.name}?`,
          negativeText: 'Cancel',
          positiveText: 'Delete',
        });
        if (confirmed) deleteItem({ itemId: item.id });
      },
    },
  ];

  if (catalogQuery.isError || attributesQuery.isError || itemsQuery.isError) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 200px)',
          paddingX: 2,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Error errorMessage="Error loading items" />
      </Box>
    );
  }
  if (
    catalogQuery.isLoading ||
    attributesQuery.isLoading ||
    itemsQuery.isLoading
  ) {
    return <PageLoading />;
  }

  const selectedViewItem = selectedViewItemId
    ? itemsById.get(selectedViewItemId)
    : null;

  const libraryRow = (_: number, row: LibraryRow) => {
    if (row.kind === 'product') {
      return (
        <LibraryProductRow
          entry={row.entry}
          isOpen={expanded.has(row.entry.product.id)}
          busy={busy}
          actions={productActions(row.entry)}
          onToggle={toggleExpanded}
        />
      );
    }
    const item = itemsById.get(row.variant.id);
    return (
      <LibraryVariantRow
        entry={row.entry}
        variant={row.variant}
        item={item}
        busy={busy}
        actions={item ? variantActions(item) : []}
        onSelect={setSelectedViewItemId}
      />
    );
  };

  const hasCatalog = catalog.length > 0;

  return (
    <>
      {hasCatalog && (
        <Stack gap={2}>
          <Box
            sx={{
              mb: 1,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'center',
            }}>
            <ItemsSearchInput
              value={query}
              onChange={setQuery}
              isDebouncing={false}
              disabled={busy}
              placeholder="Search items, brands, sizes…"
              maxWidth={340}
            />
            {filterSelect(
              'Categories',
              selection.category,
              categories.map(t => ({
                ...t,
                label: getTextForCategory(t.key as ItemCategory),
              })),
              value => selectCategory((value as ItemCategory) ?? null),
            )}
            {!!selection.category && (
              <>
                {filterSelect(
                  'Brands',
                  selection.brandId,
                  brands.map(b => ({
                    ...b,
                    label: b.key === NO_BRAND_ID ? 'No brand' : b.label,
                  })),
                  value => setSelection(c => ({ ...c, brandId: value })),
                )}
                {filterSelect('Types', selection.typeLabel, types, value =>
                  setSelection(c => ({ ...c, typeLabel: value })),
                )}
              </>
            )}
            <div className="header-right">
              <Button
                variant="contained"
                className="primary"
                onClick={() => setEditing({ kind: 'new' })}>
                New Item
              </Button>
            </div>
          </Box>
          <FilterChips
            chips={chips}
            onToggleValue={toggleAttributeValue}
            onClear={clearAttribute}
          />
          {products.length > 0 ? (
            <Box>
              <LibraryHeader />
              <Virtuoso
                useWindowScroll
                // scroll anchoring fights Virtuoso and can hide the header on the way up
                style={{ overflowAnchor: 'none' }}
                increaseViewportBy={{ top: 400, bottom: 400 }}
                data={libraryRows}
                computeItemKey={libraryRowKey}
                itemContent={libraryRow}
              />
            </Box>
          ) : (
            <OutlinedPageBox>
              <CenteredContentLayout
                image={{ src: '/price-tag.svg', alt: 'No items found' }}
                title={
                  isSearchActive
                    ? `No items found matching "${deferredQuery}"`
                    : 'No items match these filters'
                }
                description="Try a different search term or clear your filters to see all items."
                button={{
                  text: 'Clear',
                  onClick: () => {
                    setQuery('');
                    setSelection(EMPTY_SELECTION);
                  },
                }}
                maxWidth="750px"
                imageMaxWidth="120px"
              />
            </OutlinedPageBox>
          )}
        </Stack>
      )}

      {!hasCatalog && (
        <OutlinedPageBox>
          <CenteredContentLayout
            image={{ src: '/price-tag.svg', alt: 'Add an item' }}
            title="Add an item to start selling products"
            description="Add products to your item library to help speed up checkout and save your products pricing information."
            button={{
              text: 'Create an Item',
              onClick: () => setEditing({ kind: 'new' }),
            }}
            maxWidth="750px"
            imageMaxWidth="120px"
          />
        </OutlinedPageBox>
      )}

      <FullPageModal
        modalOpen={!!editing}
        name={
          editing?.kind === 'product'
            ? trimString(editing.name, 20)
            : editing?.kind === 'standalone'
            ? trimString(editing.item.name, 20)
            : 'Create an Item'
        }
        setModalOpen={() => setEditing(null)}>
        {editing && (
          <ProductForm
            productId={editing.kind === 'product' ? editing.productId : null}
            standaloneItem={editing.kind === 'standalone' ? editing.item : null}
            onSuccess={() => setEditing(null)}
          />
        )}
      </FullPageModal>
      <ItemSideBarModal
        isOpen={!!selectedViewItem}
        item={selectedViewItem}
        onClose={() => setSelectedViewItemId(null)}
      />
    </>
  );
}

export default ProductsTab;
